/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import styled from 'styled-components';

import { LoadingButton } from '@common/Button';
import { Delete, FindInPageOutlined, PolylineOutlined, SearchOffOutlined } from '@mui/icons-material';
import { useTheme } from '../../../../../../styles/theme';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { disableBucketEmbeddings, enableBucketEmbeddings, TelnyxBucket } from '@requests/buckets';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { bucketActions } from '@store/slices';
import { useDispatch } from 'react-redux';
import { DocumentLoader } from '@common/hooks/useDocument';
import { BucketFile } from '.';
import { Card } from '@common/Card';
import { Select } from '@common/Select';
import { Label } from '@common/Label';
import { Stack } from '@mui/material';
import { Option } from '@common/BucketInput/BucketField';
const HEADER_HEIGHT = 100;
const TOP_NAVBAR_HEIGHT = 61;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  gap: 0.75rem;
  height: calc(100vh - ${HEADER_HEIGHT + TOP_NAVBAR_HEIGHT}px - 16px);
  overflow: hidden;
`;

interface Props {
  setPage: React.Dispatch<number>;
  documentLoader: DocumentLoader;
  setTab: React.Dispatch<string>;
  files: BucketFile[];
  task: any | null;
  isTaskLoading: boolean;
}

export const Embed: React.FC<Props> = ({ setTab, task, isTaskLoading, setPage }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { service_id, bucketName } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [modelValue, setModelValue] = useState<string>('thenlper/gte-large');
  const loaderValue = 'default';

  const bucketQueryKey = ['globalFiles', 'bucket', { service_id, bucket_name: bucketName }];
  const bucket = queryClient.getQueryData<TelnyxBucket>(bucketQueryKey);

  const { mutateAsync: embed, status: embedStatus } = useMutation({
    mutationFn: enableBucketEmbeddings,
    onSuccess: (data) => {
      if (!data) return;

      if (data?.error) {
        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to embed bucket!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      dispatch(bucketActions.setTaskId(data.task_id));
      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });
      enqueueSnackbar('Bucket embedded succesfully!', { variant: 'success', autoHideDuration: 3000 });

      setTab('4');
    },
  });

  const isEmbedLoading = embedStatus === 'pending';

  const { mutateAsync: unembed, status: unembedStatus } = useMutation({
    mutationFn: disableBucketEmbeddings,
    onSuccess: (data) => {
      setPage(0);
      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });

      if (!data) return;

      if (data?.error) {
        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to remove bucket embeddings!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      enqueueSnackbar('Bucket embeddings removed succesfully!', { variant: 'success', autoHideDuration: 3000 });
    },
  });

  const isUnembedLoading = unembedStatus === 'pending';

  const models: Option[] = [
    { value: 'thenlper/gte-large', label: 'thenlper/gte-large' },
    { value: 'intfloat/multilingual-e5-large', label: 'intfloat/multilingual-e5-large' },
    // { value: 'sentence-transformers/all-mpnet-base-v2', label: 'sentence-transformers/all-mpnet-base-v2' },
  ];

  if (bucket?.embedded || task?.status === 'processing') {
    return (
      <Container>
        <Card
          title='Bucket Embedded'
          description='This bucket is embedded. Uploading or deleting files will automatically update the embeddings. '
          icon={<PolylineOutlined sx={{ fontSize: '2.2ch', color: theme.telnyx.blue }} />}
        />
        <Card
          title='Disable Embeddings'
          description='Disabling the embeddings for this bucket will delete all vectors from the database. This bucket will be unsearchable until re-enabled.'
          icon={<SearchOffOutlined sx={{ color: theme.telnyx.red }} />}
          actions={
            <LoadingButton
              minHeight='33px'
              fullWidth
              loading={isUnembedLoading}
              onClick={() => {
                enqueueSnackbar('Removing bucket embeddings...', {
                  variant: 'info',
                  autoHideDuration: 3000,
                });

                unembed({ bucketName: bucketName || '', service_id: service_id as string });
              }}
              customColor='red'
              startIcon={<Delete sx={{ fontSize: '2ch !important' }} />}
            >
              Disable
            </LoadingButton>
          }
        />
      </Container>
    );
  }

  return (
    <Container>
      <Card
        title='Embed Bucket'
        description='Perform embedding on this bucket using an embedding model of your choice. Embedded buckets automatically embed new files.'
        link='https://developers.telnyx.com/api/inference/inference-embedding/post-embedding'
        icon={<PolylineOutlined sx={{ fontSize: '2.2ch', color: theme.telnyx.blue }} />}
        body={
          <Stack spacing={0.5}>
            <Label title='Model' tooltip='Supported models to vectorize and embed documents.' />
            <Select
              value={modelValue}
              setValue={setModelValue}
              options={models}
              placeholder='The model used to embed the files'
            />
          </Stack>
        }
        actions={
          <LoadingButton
            loading={isEmbedLoading || task?.status === 'processing' || task?.status === 'queued' || isTaskLoading}
            customColor='primary'
            minHeight='33px'
            fullWidth
            onClick={() => {
              enqueueSnackbar('Embedding bucket...', {
                variant: 'info',
                autoHideDuration: 3000,
              });

              embed({ bucketName, model: modelValue, loader: loaderValue, service_id: service_id as string });
            }}
            startIcon={<FindInPageOutlined sx={{ fontSize: '2ch !important' }} />}
          >
            Embed Bucket
          </LoadingButton>
        }
      />
    </Container>
  );
};
